// apps/web/src/features/admin/form-config/fields/FieldRow.tsx
import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import type { FieldContent, HeaderMappingContent } from '@capture/shared';
import { classifyField } from '@capture/shared';
import { StageStrip } from './StageStrip';
import './FieldRow.css';

/**
 * Closes a popup menu on Escape or on a pointerdown outside `triggerRef`
 * AND outside `menuRef`. Mirrors the pattern in WorkflowTab.tsx
 * > useMenuDismiss, but accepts two refs because the trigger and the
 * portaled menu list live in different parts of the DOM tree (R-153).
 */
function useMenuDismiss(
  triggerRef: React.RefObject<HTMLElement | null>,
  menuRef: React.RefObject<HTMLElement | null>,
  isOpen: boolean,
  onClose: () => void,
) {
  useEffect(() => {
    if (!isOpen) return;
    function onPointerDown(e: PointerEvent) {
      const target = e.target as Node;
      const insideTrigger = triggerRef.current?.contains(target);
      const insideMenu = menuRef.current?.contains(target);
      if (!insideTrigger && !insideMenu) onClose();
    }
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') onClose();
    }
    document.addEventListener('pointerdown', onPointerDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('pointerdown', onPointerDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [triggerRef, menuRef, isOpen, onClose]);
}

/**
 * Estimated rendered height of the 3-item Edit/Duplicate/Delete menu.
 * Three ~36px rows + the list's top/bottom padding. Used as the
 * space-below threshold before the menu is rendered (so we can't yet
 * measure its real height). Conservative enough that even a smaller
 * 2-item menu flips with margin to spare. (R-192)
 */
const MENU_HEIGHT_EST = 120;
/** Keep a small gap between the menu's far edge and the viewport edge. */
const VIEWPORT_MARGIN = 8;
/** Gap between the trigger and the menu (≈ var(--space-1)). */
const TRIGGER_GAP = 4;

interface MenuAnchor {
  /** Fixed `top` for the portaled menu (px from viewport top). */
  top: number;
  /** Fixed `right` for the portaled menu (px from viewport right). */
  right: number;
  /** `bottom` → flipped above the trigger; `top` → anchored below it. */
  placement: 'top' | 'bottom';
}

/**
 * Computes the fixed-positioned coordinates for the row's ⋮ menu so
 * the portaled list sits flush against the bottom-right of the
 * trigger button. Re-measures on `isOpen` changes and on window
 * resize/scroll so the menu tracks the trigger while it's visible.
 *
 * Using `position: fixed` (not absolute) means the menu's coordinates
 * are relative to the viewport, so we don't have to account for any
 * ancestor's `position: relative` / `transform` / scroll offset. The
 * tradeoff is that a scroll on the field list re-positions the
 * trigger; we listen for that and re-measure (closing the menu when
 * the trigger leaves the viewport is the operator's expectation —
 * Radix DropdownMenu behaves the same way).
 *
 * R-192 (2026-05-28) — auto-flip: when the trigger sits too near the
 * viewport bottom for the downward menu to fit, anchor the menu ABOVE
 * the trigger instead (`placement: 'top'`). The original portal fix
 * (R-153 / 74462c1) escaped the `overflow: hidden` clip but always
 * opened downward, so a row near the bottom dropped its menu below the
 * visible viewport — operators couldn't reach Delete/Edit/Duplicate.
 */
function useTriggerAnchor(
  triggerRef: React.RefObject<HTMLElement | null>,
  isOpen: boolean,
) {
  const [anchor, setAnchor] = useState<MenuAnchor | null>(null);

  const measure = useCallback(() => {
    const el = triggerRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    const spaceBelow = window.innerHeight - rect.bottom - VIEWPORT_MARGIN;
    const flipUp = spaceBelow < MENU_HEIGHT_EST;
    setAnchor({
      // Flipped: anchor the menu's TOP above the trigger's top by the
      // estimated menu height + the gap. Default: just below the trigger.
      top: flipUp
        ? rect.top - MENU_HEIGHT_EST - TRIGGER_GAP
        : rect.bottom + TRIGGER_GAP,
      right: window.innerWidth - rect.right,
      placement: flipUp ? 'top' : 'bottom',
    });
  }, [triggerRef]);

  useLayoutEffect(() => {
    if (!isOpen) {
      setAnchor(null);
      return;
    }
    measure();
    window.addEventListener('resize', measure);
    // Capture phase so we catch scrolls on any ancestor.
    window.addEventListener('scroll', measure, true);
    return () => {
      window.removeEventListener('resize', measure);
      window.removeEventListener('scroll', measure, true);
    };
  }, [isOpen, measure]);

  return anchor;
}

interface FieldRowProps {
  field: FieldContent;
  /**
   * Kept on the prop signature so existing call sites compile, but the
   * row no longer renders a "selected" highlight — drawer presence is
   * the new selection indicator (WU-2 redesign).
   */
  selected?: boolean;
  errorCount: number;
  /**
   * Called when the row body is clicked — highlights the row (selection
   * indicator). Does NOT open the field editor drawer. To open the drawer
   * use the explicit Edit action in the ⋮ menu. (R-117)
   */
  onSelect: (name: string) => void;
  /**
   * Called when the "Edit" menu item is chosen. Opens the field editor drawer.
   * Optional — rows without this prop hide the Edit item.
   */
  onEdit?: (name: string) => void;
  /** Called when "Delete" menu item is chosen. Optional. */
  onDelete?: (name: string) => void;
  /** Called when "Duplicate" menu item is chosen. Optional. */
  onDuplicate?: (name: string) => void;
  /**
   * R-57 (2026-05-14) — when the row is inside a read-only surface
   * (e.g. ViewVersionModal) the cursor:pointer + click-affordance
   * is misleading because there's nothing to navigate to. Setting
   * variant="readonly" drops the cursor and tab-stop so the row
   * still keyboard-skips correctly inside read-only contexts.
   */
  variant?: 'editable' | 'readonly';
  /**
   * R-194 — the Job Type's header mapping, threaded through so the
   * StageStrip can outline the `import` dot for fields stamped from a
   * batch header on upload. Optional — read-only contexts (e.g.
   * ViewVersionModal) that don't have the JT's header mapping handy can
   * omit it and the strip degrades to the behavior + OCR-role rendering.
   */
  headerMapping?: HeaderMappingContent | null;
}

export function FieldRow({
  field,
  errorCount,
  onSelect,
  onEdit,
  onDelete,
  onDuplicate,
  variant = 'editable',
  headerMapping,
}: FieldRowProps) {
  const kind = classifyField(field);
  const readonly = variant === 'readonly';
  const [menuOpen, setMenuOpen] = useState(false);
  // Two refs because the menu list lives in a Portal sibling of <body>,
  // so a single-ref outside-click dismiss would close the menu the
  // moment the operator clicks one of its items. (R-153 portal fix.)
  const triggerRef = useRef<HTMLButtonElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  useMenuDismiss(triggerRef, menuRef, menuOpen, useCallback(() => setMenuOpen(false), []));
  // Anchor coords for the portaled menu (fixed-positioned to the
  // viewport so it escapes the `overflow: hidden` chain on the
  // Fields tab — see R-153).
  const anchor = useTriggerAnchor(triggerRef, menuOpen);

  const hasMenuItems = !readonly && (onEdit != null || onDelete != null || onDuplicate != null);
  const hasErrors = errorCount > 0;
  // R-169 (2026-05-26) — when a row has validation errors AND the
  // caller passed `onEdit`, clicking the errors badge opens the
  // editor drawer (instead of just highlighting the row like a normal
  // body click would). TODO(post-walk): route the drawer to the
  // Validation tab in a future bundle — the drawer doesn't currently
  // expose a "focused tab" entry point.
  const errorsCellOpensDrawer = hasErrors && !readonly && onEdit != null;
  // We intercept the body button's onClick: if the click bubbled up
  // from inside the errors cell, route to `onEdit`; otherwise the
  // legacy `onSelect` (highlight-only) behavior.
  const errorsCellRef = useRef<HTMLSpanElement>(null);
  const handleBodyClick = (e: React.MouseEvent<HTMLButtonElement>) => {
    if (readonly) return;
    if (
      errorsCellOpensDrawer &&
      errorsCellRef.current &&
      errorsCellRef.current.contains(e.target as Node)
    ) {
      onEdit!(field.name);
      return;
    }
    onSelect(field.name);
  };

  return (
    <div
      role="row"
      className={`field-row${hasErrors ? ' field-row--has-errors' : ''}${readonly ? ' field-row--readonly' : ''}`}
      data-field-name={field.name}
      aria-disabled={readonly ? true : undefined}
      tabIndex={readonly ? -1 : undefined}
    >
      {/* Row body — click to select/highlight; does NOT open drawer (R-117).
       *  R-169: clicks on `.field-row__errors` ARE routed to `onEdit` so
       *  operators can jump straight into the drawer to fix the offending
       *  validator. */}
      <button
        type="button"
        className="field-row__body"
        aria-label={`Select field ${field.name}`}
        onClick={readonly ? undefined : handleBodyClick}
        tabIndex={readonly ? -1 : undefined}
        aria-disabled={readonly || undefined}
      >
        <span className="field-row__name">
          {field.name}
          {kind === 'header' && (
            <span className="field-row__chip field-row__chip--header" aria-label="Header-mapped field">
              Header
            </span>
          )}
        </span>
        <span className="field-row__label">{field.label}</span>
        <span className="field-row__type">{field.dataType}</span>
        <span
          className={`field-row__scope field-row__chip field-row__chip--scope-${field.scope}`}
          aria-label={field.scope === 'batch' ? 'Batch-scoped field' : 'Document-scoped field'}
        >
          {field.scope === 'batch' ? 'BATCH' : 'DOC'}
        </span>
        <StageStrip
          stageMap={field.stageMap}
          internalName={field.internalName ?? field.name}
          ocrExtracted={field.ocrExtracted}
          ocrAliases={field.ocrAliases}
          headerMapping={headerMapping}
        />
        <span
          ref={errorsCellRef}
          className={`field-row__errors${errorsCellOpensDrawer ? ' field-row__errors--clickable' : ''}`}
          aria-label={hasErrors ? `${errorCount} errors — click to edit field` : undefined}
          title={errorsCellOpensDrawer ? 'Click to edit field validators' : undefined}
        >
          {hasErrors ? errorCount : ''}
        </span>
      </button>

      {/* R-117 + R-153: ⋮ row menu for Edit / Duplicate / Delete.
       *  The list is portaled to `document.body` so the `overflow: hidden`
       *  chain that the Fields tab uses to own its scrollbars doesn't
       *  clip the menu when it opens near the bottom of the list. */}
      {hasMenuItems && (
        <div className="field-row__menu">
          <button
            ref={triggerRef}
            type="button"
            className="field-row__menu-trigger"
            aria-label={`Row menu for ${field.name}`}
            aria-haspopup="menu"
            aria-expanded={menuOpen}
            onClick={(e) => {
              e.stopPropagation();
              setMenuOpen((o) => !o);
            }}
          >
            ⋮
          </button>
          {menuOpen && anchor && createPortal(
            <div
              ref={menuRef}
              role="menu"
              className={`field-row__menu-list field-row__menu-list--portaled field-row__menu-list--${anchor.placement}`}
              data-placement={anchor.placement}
              style={{ top: anchor.top, right: anchor.right }}
            >
              {onEdit && (
                <button
                  type="button"
                  role="menuitem"
                  onClick={() => {
                    setMenuOpen(false);
                    onEdit(field.name);
                  }}
                >
                  Edit
                </button>
              )}
              {onDuplicate && (
                <button
                  type="button"
                  role="menuitem"
                  onClick={() => {
                    setMenuOpen(false);
                    onDuplicate(field.name);
                  }}
                >
                  Duplicate
                </button>
              )}
              {onDelete && (
                <button
                  type="button"
                  role="menuitem"
                  onClick={() => {
                    setMenuOpen(false);
                    onDelete(field.name);
                  }}
                >
                  Delete
                </button>
              )}
            </div>,
            document.body,
          )}
        </div>
      )}
    </div>
  );
}
